import { useMemo } from 'react';
import { UseFormValidationParams, UseFormValidationReturn, CreateCorrectionData } from '../types';

export const useFormValidation = ({
    selectedUser,
    selectedTask,
    date,
    correctionHours,
    comment,
    assignmentInfo,
    selectedActivity,
    customRate,
}: UseFormValidationParams): UseFormValidationReturn => {
    // Validation logic
    const isValid = useMemo(() => {
        const hasBasicFields = selectedUser && selectedTask && date && correctionHours !== '' && comment.trim();
        if (!hasBasicFields) return false;

        // If assignment requires rate selection, ensure activity or custom rate is provided
        if (assignmentInfo?.requiresActivityOrRate) {
            return !!(selectedActivity || customRate);
        }

        return true;
    }, [selectedUser, selectedTask, date, correctionHours, comment, assignmentInfo, selectedActivity, customRate]);

    const validateAndGetData = (): {
        isValid: boolean;
        data?: CreateCorrectionData;
        error?: string;
    } => {
        if (!isValid || !selectedTask || !selectedUser || !date) {
            return {
                isValid: false,
                error: 'Please fill in all required fields',
            };
        }

        const hoursFloat = Number.parseFloat(correctionHours);
        if (Number.isNaN(hoursFloat)) {
            return {
                isValid: false,
                error: 'Please enter a valid number of hours',
            };
        }

        const customRateFloat = customRate ? Number.parseFloat(customRate) : undefined;
        if (customRate && Number.isNaN(customRateFloat)) {
            return {
                isValid: false,
                error: 'Please enter a valid custom rate',
            };
        }

        return {
            isValid: true,
            data: {
                userId: selectedUser.id,
                taskId: selectedTask.taskId,
                date: date.removeTimeZone().toISOString(),
                correctionHours: hoursFloat,
                comment: comment.trim(),
                activityId: selectedActivity?.id,
                customRate: customRateFloat,
            },
        };
    };

    return {
        isValid,
        validateAndGetData,
    };
};
